import React from 'react';
import Container from './Container';

interface ServicePageSectionProps {
  id?: string;
  title?: string;
  subtitle?: string;
  bgColor?: 'white' | 'gray' | 'primary' | 'gradient';
  padding?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  className?: string;
}

/**
 * Standardized section component for service pages with consistent styling and spacing
 */
const ServicePageSection: React.FC<ServicePageSectionProps> = ({
  id,
  title,
  subtitle,
  bgColor = 'white',
  padding = 'md',
  children,
  className = '',
}) => {
  const getBgClass = () => {
    switch (bgColor) {
      case 'gray':
        return 'bg-neutral-50';
      case 'primary':
        return 'bg-primary text-white';
      case 'gradient':
        return 'bg-gradient-to-br from-primary-dark via-primary to-secondary text-white';
      default:
        return 'bg-white';
    }
  };

  const getPaddingClass = () => {
    switch (padding) {
      case 'sm':
        return 'py-8 md:py-12';
      case 'lg':
        return 'py-16 md:py-20';
      default:
        return 'py-12 md:py-16';
    }
  };

  const textColor = bgColor === 'primary' || bgColor === 'gradient' ? 'text-white' : 'text-neutral-900';

  return (
    <section 
      id={id}
      className={`${getBgClass()} ${getPaddingClass()} ${className}`}
    >
      <Container>
        {(title || subtitle) && (
          <div className="text-center mb-12">
            {title && (
              <h2 className={`text-2xl md:text-3xl font-bold mb-4 ${textColor}`}>
                {title}
              </h2>
            )}
            {subtitle && (
              <p className={`text-lg max-w-3xl mx-auto ${textColor}/90`}>
                {subtitle}
              </p>
            )}
          </div>
        )}
        {children}
      </Container>
    </section>
  );
};

export default ServicePageSection;